import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  OfflineBolt,
  DeviceHub,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../services/firebase';

// ─── Types ───────────────────────────────────────────────────────────────────

interface IoTStatDoc {
  bucket?: string;
  hour?: string;
  btDeviceCount?: number;
  scanCount?: number;
  updatedAt?: any;
  venueId: string;
  venueAdminId: string;
  deviceId?: string;
  zone?: string;
}

interface ChartDataPoint {
  label: string;
  bucket: string;
  [zoneKey: string]: number | string;
}

interface DeviceRecord {
  deviceId: string;
  zone: string;
  status: 'online' | 'offline';
  lastSeen?: any;
  cpuTempC?: number;
  hostname?: string;
}

interface ZoneSummary {
  zone: string;
  current: number;
  peak: number;
  avg: number;
  colour: string;
}

type Granularity = '15min' | 'hourly' | 'daily';
type TimeRange = '3h' | '24h' | '7d' | '30d';

interface VenueIoTAnalyticsProps {
  venueId: string;
  venueName: string;
  venueAdminId: string;
}

const ZONE_COLOURS = [
  '#6c63ff',
  '#16a76a',
  '#f59e0b',
  '#e040fb',
  '#3b82f6',
  '#ef4444',
  '#14b8a6',
  '#8a94a6',
];

const GRID_STYLE = { stroke: '#eef0f5', strokeDasharray: '4 4' };
const AXIS_STYLE = { tick: { fill: '#8a94a6', fontSize: 11 }, axisLine: { stroke: '#e8eaf0' }, tickLine: false };
const TOOLTIP_STYLE = {
  contentStyle: { background: '#fff', border: '1px solid #e8eaf0', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: 12, padding: '10px 14px' },
  labelStyle: { fontWeight: 600, color: '#1a1d23', marginBottom: 4 },
};

function getBucketStart(range: TimeRange): Date {
  const now = new Date();
  switch (range) {
    case '3h':  return new Date(now.getTime() - 3  * 60 * 60 * 1000);
    case '24h': return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case '7d':  return new Date(now.getTime() - 7  * 24 * 60 * 60 * 1000);
    case '30d': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }
}

function formatBucketLabel(bucket: string, granularity: Granularity): string {
  const d = new Date(bucket);
  switch (granularity) {
    case '15min':
    case 'hourly':
      return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
    case 'daily':
      return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  }
}

function aggregateByZone(
  docs: IoTStatDoc[],
  granularity: Granularity,
  zones: string[],
): ChartDataPoint[] {
  const bucketZoneMap = new Map<string, Map<string, number>>();

  for (const d of docs) {
    const rawKey = d.bucket || d.hour || '';
    if (!rawKey) continue;

    const date = new Date(rawKey);
    let bucketKey: string;
    if (granularity === '15min') {
      bucketKey = rawKey;
    } else if (granularity === 'hourly') {
      date.setMinutes(0, 0, 0);
      bucketKey = date.toISOString();
    } else {
      date.setHours(0, 0, 0, 0);
      bucketKey = date.toISOString();
    }

    const zoneKey = d.zone || 'default';
    // BT only — no WiFi
    const count = d.btDeviceCount ?? 0;

    if (!bucketZoneMap.has(bucketKey)) bucketZoneMap.set(bucketKey, new Map());
    const zoneMap = bucketZoneMap.get(bucketKey)!;
    zoneMap.set(zoneKey, Math.max(zoneMap.get(zoneKey) ?? 0, count));
  }

  return Array.from(bucketZoneMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucketKey, zoneMap]) => {
      const point: ChartDataPoint = {
        label:  formatBucketLabel(bucketKey, granularity),
        bucket: bucketKey,
      };
      for (const z of zones) {
        point[z] = zoneMap.get(z) ?? 0;
      }
      return point;
    });
}

function isOnline(lastSeen: any): boolean {
  if (!lastSeen) return false;
  const ts = lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen);
  return Date.now() - ts.getTime() < 3 * 60 * 1000;
}

const VenueIoTAnalytics: React.FC<VenueIoTAnalyticsProps> = ({
  venueId,
  venueName,
  venueAdminId,
}) => {
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [hasDevice, setHasDevice]         = useState(false);
  const [devices, setDevices]             = useState<DeviceRecord[]>([]);
  const [zones, setZones]                 = useState<string[]>([]);
  const [rawDocs, setRawDocs]             = useState<IoTStatDoc[]>([]);
  const [chartData, setChartData]         = useState<ChartDataPoint[]>([]);
  const [zoneSummaries, setZoneSummaries] = useState<ZoneSummary[]>([]);
  const [timeRange, setTimeRange]         = useState<TimeRange>('24h');
  const [granularity, setGranularity]     = useState<Granularity>('hourly');

  useEffect(() => {
    const checkDevices = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'devices'),
          where('venueId', '==', venueId),
        ));

        if (snap.empty) {
          setHasDevice(false);
          setLoading(false);
          return;
        }

        setHasDevice(true);
        const devList: DeviceRecord[] = snap.docs.map(d => {
          const data = d.data();
          return {
            deviceId:  data.deviceId || d.id,
            zone:      data.zone || data.hostname || d.id,
            status:    isOnline(data.lastSeen) ? 'online' : 'offline',
            lastSeen:  data.lastSeen,
            cpuTempC:  data.cpuTempC,
            hostname:  data.hostname,
          };
        });

        setDevices(devList);
      } catch (e: any) {
        setError('Failed to check device status.');
        setLoading(false);
      }
    };
    checkDevices();
  }, [venueId]);

  useEffect(() => {
    if (!hasDevice) return;
    loadStats();
  }, [hasDevice, timeRange]);

  useEffect(() => {
    if (rawDocs.length > 0 && zones.length > 0) {
      const data = aggregateByZone(rawDocs, granularity, zones);
      setChartData(data);
      setZoneSummaries(buildZoneSummaries(data, zones));
    }
  }, [granularity, rawDocs, zones]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      const since = getBucketStart(timeRange).toISOString();

      const [snapBucket, snapHour] = await Promise.all([
        getDocs(query(
          collection(db, 'venues', venueId, 'iot_stats'),
          where('bucket', '>=', since),
          orderBy('bucket', 'asc'),
        )).catch(() => null),
        getDocs(query(
          collection(db, 'venues', venueId, 'iot_stats'),
          where('hour', '>=', since),
          orderBy('hour', 'asc'),
        )).catch(() => null),
      ]);

      const seenIds = new Set<string>();
      const docs: IoTStatDoc[] = [];
      for (const snap of [snapBucket, snapHour]) {
        if (!snap) continue;
        for (const d of snap.docs) {
          if (!seenIds.has(d.id)) {
            seenIds.add(d.id);
            docs.push(d.data() as IoTStatDoc);
          }
        }
      }

      const zoneSet = new Set<string>();
      for (const d of docs) {
        zoneSet.add(d.zone || 'default');
      }
      const zoneList = Array.from(zoneSet).sort();

      setRawDocs(docs);
      setZones(zoneList);

      const data = aggregateByZone(docs, granularity, zoneList);
      setChartData(data);
      setZoneSummaries(buildZoneSummaries(data, zoneList));
    } catch (e: any) {
      setError('Failed to load sensor data.');
    } finally {
      setLoading(false);
    }
  };

  const buildZoneSummaries = (data: ChartDataPoint[], zoneList: string[]): ZoneSummary[] => {
    return zoneList.map((zone, idx) => {
      const values = data.map(d => (d[zone] as number) ?? 0);
      // Use the most recent non-zero value as "current" — last bucket may be empty
      // if the aggregation window just rolled over
      const lastNonZero = [...values].reverse().find(v => v > 0) ?? 0;
      const current = values.length > 0 ? (values[values.length - 1] > 0 ? values[values.length - 1] : lastNonZero) : 0;
      const peak    = values.length > 0 ? Math.max(...values) : 0;
      const avg     = values.length > 0 ? Math.round(values.reduce((s, v) => s + v, 0) / values.length) : 0;
      return { zone, current, peak, avg, colour: ZONE_COLOURS[idx % ZONE_COLOURS.length] };
    });
  };

  const handleTimeRangeChange = (_: any, val: TimeRange | null) => {
    if (!val) return;
    if (val === '3h')       setGranularity('15min');
    else if (val === '24h') setGranularity('hourly');
    else                    setGranularity('daily');
    setTimeRange(val);
  };

  const handleGranularityChange = (e: SelectChangeEvent) => {
    setGranularity(e.target.value as Granularity);
  };

  const combinedCurrent = zoneSummaries.reduce((s, z) => s + z.current, 0);
  const combinedPeak    = chartData.length > 0
    ? Math.max(...chartData.map(d => zones.reduce((s, z) => s + ((d[z] as number) ?? 0), 0)))
    : 0;

  if (!loading && !hasDevice) {
    return (
      <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, background: '#f5f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DeviceHub sx={{ fontSize: 18, color: '#8a94a6' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="#1a1d23">IoT Sensor Analytics</Typography>
          </Box>
          <Box sx={{ p: 2.5, borderRadius: 2, background: '#f8f9fc', border: '1px solid #e8eaf0' }}>
            <Typography variant="body2" fontWeight={600} color="#1a1d23" gutterBottom>
              No sensor deployed at {venueName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A JamApp IoT sensor can track real-time patron counts using Bluetooth detection.
              Contact JamApp to get a sensor deployed at this venue.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const toggleStyles = {
    '& .MuiToggleButton-root': {
      border: '1px solid #e0e3ea', px: 1.5, py: 0.5,
      fontSize: '0.75rem', fontWeight: 500, color: '#8a94a6', textTransform: 'none',
      '&.Mui-selected': { background: '#6c63ff', color: '#fff', borderColor: '#6c63ff', '&:hover': { background: '#5b52e0' } },
    },
  };

  return (
    <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Box sx={{ width: 36, height: 36, borderRadius: 2, background: '#f5f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DeviceHub sx={{ fontSize: 18, color: '#6c63ff' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#1a1d23">IoT Sensor — Patron Count</Typography>
              <Typography variant="caption" color="text.secondary">{venueName}</Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {devices.map(dev => (
                <Tooltip
                  key={dev.deviceId}
                  title={dev.lastSeen
                    ? `Last seen: ${dev.lastSeen.toDate ? dev.lastSeen.toDate().toLocaleTimeString('en-AU') : 'Unknown'}`
                    : 'Never seen'}
                >
                  <Chip
                    size="small"
                    icon={dev.status === 'online' ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : <OfflineBolt sx={{ fontSize: '14px !important' }} />}
                    label={`${dev.zone} · ${dev.status}`}
                    color={dev.status === 'online' ? 'success' : 'error'}
                    variant="outlined"
                    sx={{ height: 24, fontSize: '0.72rem' }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
          <ToggleButtonGroup value={timeRange} exclusive onChange={handleTimeRangeChange} size="small" sx={toggleStyles}>
            <ToggleButton value="3h">3H</ToggleButton>
            <ToggleButton value="24h">24H</ToggleButton>
            <ToggleButton value="7d">7D</ToggleButton>
            <ToggleButton value="30d">30D</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {devices.length > 0 && (
          <Box display="flex" gap={3} mb={2.5} flexWrap="wrap">
            {devices.map(dev => (
              <Typography key={dev.deviceId} variant="caption" color="text.secondary">
                <strong>{dev.hostname || dev.deviceId}</strong>
                {dev.cpuTempC != null && ` · ${dev.cpuTempC}°C`}
              </Typography>
            ))}
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress size={36} sx={{ color: '#6c63ff' }} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : chartData.length === 0 ? (
          <Box sx={{ p: 3, borderRadius: 2, background: '#f8f9fc', border: '1px solid #e8eaf0', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No sensor data yet for this time range. The sensor collects data every 15 minutes.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Zone stat cards */}
            <Grid container spacing={2} mb={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ p: 2, borderRadius: 2, background: '#f8f9fc', border: '1px solid #e8eaf0', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a94a6', mb: 0.5 }}>Total Current</Typography>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 700, color: '#1a1d23', lineHeight: 1 }}>{combinedCurrent}</Typography>
                  <Typography variant="caption" color="text.secondary">Peak: {combinedPeak}</Typography>
                </Box>
              </Grid>
              {zoneSummaries.map(z => (
                <Grid key={z.zone} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ p: 2, borderRadius: 2, background: `${z.colour}08`, border: `1px solid ${z.colour}25`, textAlign: 'center', borderLeft: `3px solid ${z.colour}` }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: z.colour, mb: 0.5 }}>{z.zone}</Typography>
                    <Typography sx={{ fontSize: '1.8rem', fontWeight: 700, color: z.colour, lineHeight: 1 }}>{z.current}</Typography>
                    <Typography variant="caption" color="text.secondary">Peak: {z.peak} · Avg: {z.avg}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Granularity selector */}
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>Granularity</InputLabel>
                <Select value={granularity} label="Granularity" onChange={handleGranularityChange} sx={{ fontSize: '0.8rem' }}>
                  <MenuItem value="15min">15 Minutes</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  {zones.map((zone, idx) => (
                    <linearGradient key={zone} id={`iot-gradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ZONE_COLOURS[idx % ZONE_COLOURS.length]} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={ZONE_COLOURS[idx % ZONE_COLOURS.length]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid {...GRID_STYLE} vertical={false} />
                <XAxis dataKey="label" {...AXIS_STYLE} interval="preserveStartEnd" />
                <YAxis {...AXIS_STYLE} width={32} label={{ value: 'Devices', angle: -90, position: 'insideLeft', offset: 10, fontSize: 11, fill: '#8a94a6' }} />
                <RechartsTooltip {...TOOLTIP_STYLE} />
                {zones.length > 1 && (
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                )}
                {zones.map((zone, idx) => (
                  <Area key={zone} type="monotone" dataKey={zone} name={zone}
                    stroke={ZONE_COLOURS[idx % ZONE_COLOURS.length]}
                    fill={`url(#iot-gradient-${idx})`}
                    strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                ))}
              </AreaChart>
            </ResponsiveContainer>

            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1.5}>
              {zones.length} zone{zones.length !== 1 ? 's' : ''} · Bluetooth detection · Data collected every 15 minutes
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default VenueIoTAnalytics;